import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { appUrl } from '@astro/config'
import { useDeleteLiveClass, useInstructorCourses, useLiveClasses } from '../useDashboard'
import {
  Badge,
  ClassSkeletonCard,
  ConfirmDeleteClassModal,
  DeleteClassButton,
  InstructorsRow,
  MetaChip,
  RescheduleClassButton,
  SectionHead,
  SkeletonGrid,
  StatTile,
  hostHref,
  isRecordedRow,
  isScheduledRow,
} from './academyShared'
import EnrolledCoursesSections from './EnrolledCoursesSections'
import InstructorCourseDetails from './InstructorCourseDetails'
import CreateLiveClassModal from './CreateLiveClassModal'
import CreateCourseModal from './CreateCourseModal'
import RescheduleLiveClassModal from './RescheduleLiveClassModal'

const instructorConsoleHref = appUrl('education', '/instructor/dashboard')

const LIVE_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'live', label: 'Live Now' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'past', label: 'Past' },
]

/* ── type 1: live-session cards ── */

/** One live-session card — date block, status pill, schedule row; a class
 * that is on air right now gets the red treatment and a console CTA.
 * `onDelete` appends the delete affordance to the footer; `onReschedule`
 * appends the reschedule affordance (scheduled sessions only). */
/* Cards are flex columns pinned to h-full: the grid stretches every card to the
   tallest in its row, so without this the surplus height opened as dead space
   mid-card instead of letting the footer settle at the bottom. */
function LiveClassCard({ c, onDelete, onReschedule }) {
  return (
    <div className={`flex h-full flex-col rounded-2xl border p-4 transition-all hover:shadow-md ${c.isLive ? 'border-red-200 bg-red-50/40' : 'border-stone-100 hover:border-[#D4A017]/40'}`}>
      <div className="flex gap-3 items-start">
        <div className={`text-white w-12 h-12 sm:w-14 sm:h-14 rounded-xl flex flex-col items-center justify-center shrink-0 shadow-lg ${c.isLive ? 'bg-[#3D0A0A] shadow-[#3D0A0A]/20' : 'bg-[#D48806] shadow-amber-600/20'}`}>
          <span className="text-base font-bold leading-none">{c.day}</span>
          <span className="text-[8px] uppercase tracking-widest opacity-80 mt-0.5">{c.mon}</span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-astroDark leading-tight truncate">{c.title}</p>
              <p className={`text-[11px] mt-0.5 truncate ${c.course ? 'text-stone-500' : 'text-stone-400'}`}>
                {c.course || 'Standalone class'}
              </p>
            </div>
            <span className={`inline-flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 whitespace-nowrap ${c.cls}`}>
              {c.isLive && <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />}
              {c.status}
            </span>
          </div>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-3 mb-3 text-[10px] text-stone-400 uppercase tracking-wide font-bold">
        <span>🗓 {c.dateLabel}</span>
        {c.time ? <span>🕐 {c.time}{c.endTime ? ` – ${c.endTime}` : ''}</span> : null}
        {c.dur ? <span>⏳ {c.dur}</span> : null}
        {c.isRecorded ? <span>🎥 Will be recorded</span> : null}
      </div>
      {c.id != null && (
        <div className={`flex items-center justify-between gap-2 mt-auto pt-3 border-t ${c.isLive ? 'border-red-100' : 'border-stone-100'}`}>
          {c.isLive || isScheduledRow(c) ? (
            <a
              href={hostHref(c.id)}
              className={`text-[10px] font-bold uppercase tracking-wider hover:underline ${c.isLive ? 'text-red-600' : 'text-goldAccent'}`}
            >
              {c.isLive ? '● Rejoin & Host →' : 'Go Live & Host →'}
            </a>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wider text-stone-300">Session over</span>
          )}
          <span className="flex items-center gap-2">
            {onReschedule && isScheduledRow(c) && <RescheduleClassButton c={c} onReschedule={onReschedule} />}
            {onDelete && <DeleteClassButton c={c} onDelete={onDelete} />}
          </span>
        </div>
      )}
    </div>
  )
}

/* ── type 2: recordings-library cards ── */

/** One recordings-library card — ready recordings link out to watch; a class
 * flagged is_recorded whose file hasn't landed yet shows as processing.
 * `onDelete` appends the delete affordance to the footer. */
function RecordedClassCard({ c, onDelete }) {
  const ready = Boolean(c.recordingUrl)
  return (
    <div className="flex h-full flex-col rounded-2xl border border-stone-100 p-4 hover:shadow-md hover:border-[#D4A017]/40 transition-all">
      <div className="flex gap-3 items-start">
        <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-stone-100 text-xl flex items-center justify-center shrink-0">🎥</div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-sm font-bold text-astroDark leading-tight truncate">{c.title}</p>
              <p className={`text-[11px] mt-0.5 truncate ${c.course ? 'text-stone-500' : 'text-stone-400'}`}>
                {c.course || 'Standalone class'}
              </p>
            </div>
            <span className={`text-[9px] font-bold uppercase tracking-wide px-2 py-1 rounded-full shrink-0 ${ready ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
              {ready ? 'Recording Ready' : 'Processing'}
            </span>
          </div>
        </div>
      </div>
      <div className="mt-3" />
      <div className="flex flex-wrap items-center justify-between gap-2 mt-auto pt-3 border-t border-stone-100">
        <span className="text-[10px] text-stone-400 uppercase tracking-wide font-bold">🗓 {c.dateLabel}{c.dur ? ` · ⏳ ${c.dur}` : ''}</span>
        <span className="flex items-center gap-2">
          {ready ? (
            <a
              href={c.recordingUrl}
              target="_blank"
              rel="noreferrer"
              className="text-[10px] font-bold uppercase tracking-wider text-goldAccent hover:underline"
            >
              View Recording →
            </a>
          ) : (
            <span className="text-[10px] font-bold uppercase tracking-wider text-stone-300">Available after processing</span>
          )}
          {onDelete && c.id != null && <DeleteClassButton c={c} onDelete={onDelete} />}
        </span>
      </div>
    </div>
  )
}

/* ── type 3: course cards ── */

/** One course card — the details-page hero look: cover on the left, then the
 * badge row, royal title, instructor avatars and meta chips. The whole card
 * opens the course's dedicated details page. */
function CourseCard({ course, sessions, onOpen }) {
  const liveCount = sessions.filter((c) => c.isLive).length
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group text-left w-full rounded-2xl border border-stone-100 p-5 hover:shadow-md hover:border-[#D4A017]/40 transition-all"
    >
      <div className="flex flex-col sm:flex-row gap-5">
        {course.img ? (
          <img src={course.img} alt="" className="w-full sm:w-44 h-32 rounded-2xl object-cover shrink-0" />
        ) : (
          <div className="w-full sm:w-44 h-32 rounded-2xl bg-[#F5E6D3] text-[#A67C00] text-4xl flex items-center justify-center shrink-0">
            📚
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-1.5">
            {course.category && <Badge>{course.category}</Badge>}
            {course.level && <Badge className="bg-stone-100 text-stone-500 capitalize">{course.level}</Badge>}
            <Badge className={course.isLiveCourse ? 'bg-red-50 text-red-500' : 'bg-blue-50 text-blue-600'}>
              {course.isLiveCourse ? '🔴 Live Course' : '🎬 Recorded'}
            </Badge>
            {liveCount > 0 && (
              <Badge className="bg-red-50 text-red-500">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse mr-1" />
                {liveCount} Live Now
              </Badge>
            )}
          </div>
          <h4 className="text-2xl text-astroDark font-royal mt-2 leading-tight group-hover:text-[#A67C00] transition-colors">
            {course.title}
          </h4>
          <InstructorsRow instructors={course.instructors} />
          <div className="flex flex-wrap items-center gap-1.5 mt-3">
            <MetaChip icon="💰">
              {course.priceLabel}
              {course.oldPriceLabel && <s className="text-stone-300 ml-1">{course.oldPriceLabel}</s>}
            </MetaChip>
            {course.lastUpdated && <MetaChip icon="🗓">Updated {course.lastUpdated}</MetaChip>}
            {course.visibility && (
              <MetaChip icon={course.visibility === 'public' ? '🌐' : '🔒'}>{course.visibility}</MetaChip>
            )}
          </div>
        </div>
      </div>
    </button>
  )
}

/* DUAL ROLE (instructor + student): the Academy tab resolves to ONE screen
   per role (tabRole picks instructor, which outranks student), which used to
   hide a teaching account's own enrolments completely. The teaching content
   still wins — it owns the whole screen — and the student side comes back as
   just the two enrolment sections appended below it. */
export default function InstructorCoursesTab({ roles = [] }) {
  const alsoStudent = roles.includes('student')
  const { classes, total, hasClasses, isLoading, isError, refetch } = useLiveClasses()
  const [showCreate, setShowCreate] = useState(false)
  const [showCreateCourse, setShowCreateCourse] = useState(false)
  const [liveFilter, setLiveFilter] = useState('all')

  // Live-class delete: the normalised row awaiting confirmation (null = closed).
  const [classToDelete, setClassToDelete] = useState(null)
  const [classDeleteError, setClassDeleteError] = useState(null)
  const deleteClassMutation = useDeleteLiveClass()
  const askDeleteClass = (c) => { setClassDeleteError(null); setClassToDelete(c) }
  // Live-class reschedule: the normalised row being moved (null = closed).
  const [classToReschedule, setClassToReschedule] = useState(null)
  const confirmDeleteClass = () =>
    deleteClassMutation.mutate(classToDelete.id, {
      onSuccess: () => setClassToDelete(null),
      onError: (err) => setClassDeleteError(err?.message || 'Could not delete the class. Please try again.'),
    })

  // The open course lives in the URL (?tab=courses&course=12) so a reload or
  // shared link lands straight on that course's details page, and the back
  // button returns to this list.
  const [searchParams, setSearchParams] = useSearchParams()
  const openCourseId = searchParams.get('course')
  const openCourse = (id) => {
    const next = new URLSearchParams(searchParams)
    next.set('course', String(id))
    setSearchParams(next)
  }
  const closeCourse = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('course')
    setSearchParams(next)
  }

  // Courses come from the course API (every course this instructor teaches);
  // live classes come from the live-class API and nest under them by course_id.
  const {
    courses: myCourses,
    hasCourses,
    isLoading: coursesLoading,
    isError: coursesError,
    refetch: refetchCourses,
  } = useInstructorCourses()

  // ── Details page takes over the whole tab when a course is open. ──
  if (openCourseId) {
    return (
      <InstructorCourseDetails
        courseId={openCourseId}
        seed={myCourses.find((c) => String(c.id) === String(openCourseId)) ?? null}
        onBack={closeCourse}
      />
    )
  }

  const recordedClasses = classes.filter(isRecordedRow)
  // Live sessions: on-air first, then API order (stable sort keeps the rest).
  const liveClasses = classes
    .filter((c) => !isRecordedRow(c))
    .sort((a, b) => Number(b.isLive) - Number(a.isLive))
  // Bucket live classes under their course_id for the per-card session counts.
  // Classes without a course still show in the Live/Recorded sections below.
  const classesByCourseId = new Map()
  for (const c of classes) {
    if (c.courseId == null) continue
    const cid = String(c.courseId)
    if (!classesByCourseId.has(cid)) classesByCourseId.set(cid, [])
    classesByCourseId.get(cid).push(c)
  }
  const courseCount = myCourses.length
  const liveNow = classes.filter((c) => c.isLive).length
  const scheduledCount = liveClasses.filter(isScheduledRow).length

  const stats = [
    { key: 'courses', icon: '📚', label: 'Courses', value: courseCount },
    { key: 'total', icon: '📼', label: 'Total Classes', value: total || classes.length },
    { key: 'live', icon: '🔴', label: 'Live Now', value: liveNow, accent: 'text-red-500' },
    { key: 'scheduled', icon: '📅', label: 'Scheduled', value: scheduledCount },
    { key: 'recordings', icon: '🎥', label: 'Recordings', value: recordedClasses.length },
  ]

  const filterCounts = {
    all: liveClasses.length,
    live: liveClasses.filter((c) => c.isLive).length,
    scheduled: scheduledCount,
    past: liveClasses.filter((c) => !c.isLive && !isScheduledRow(c)).length,
  }
  const visibleLive = liveClasses.filter((c) => {
    if (liveFilter === 'live') return c.isLive
    if (liveFilter === 'scheduled') return isScheduledRow(c)
    if (liveFilter === 'past') return !c.isLive && !isScheduledRow(c)
    return true
  })

  return (
    <div className="space-y-6">

      {/* ── Teaching header — identity, primary action, and the stat strip ── */}
      <div className="dash-card p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
          <span className="eyebrow text-amber-700">Vedic Academy · Teaching</span>
          <a href={instructorConsoleHref} className="text-[10px] uppercase font-bold tracking-wider text-goldAccent hover:underline">
            Open Instructor Console
          </a>
        </div>
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
          <div>
            <h3 className="text-xl text-astroDark font-royal">My Courses</h3>
            <p className="text-sm text-stone-400 mt-1">
              Every course you teach — open one to manage its lessons, live classes and settings.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowCreateCourse(true)}
              className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all w-max"
            >
              + Create Course
            </button>
            <button
              type="button"
              onClick={() => setShowCreate(true)}
              className="border border-stone-200 text-stone-700 font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-stone-50 transition-all w-max"
            >
              + Live Class
            </button>
          </div>
        </div>
        {(hasClasses || hasCourses || isLoading || coursesLoading) && (
          <div className="flex flex-wrap gap-3 mt-5 pt-5 border-t border-stone-100">
            {stats.map((s) => (
              <StatTile
                key={s.key}
                icon={s.icon}
                label={s.label}
                value={(s.key === 'courses' ? coursesLoading : isLoading) ? '—' : s.value}
                accent={s.accent}
              />
            ))}
          </div>
        )}
      </div>

      {isError && coursesError ? (
        <div className="dash-card p-4 sm:p-6">
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">🛰️</div>
            <p className="text-base font-semibold text-astroDark mb-1">Couldn&rsquo;t load your academy</p>
            <p className="text-sm text-stone-400 mb-4">Something went wrong while fetching your classes and courses. Please try again.</p>
            <button
              type="button"
              onClick={() => { refetch(); refetchCourses() }}
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-6 py-3 rounded-full border transition-colors"
              style={{ borderColor: 'rgba(180,83,9,.35)', color: '#B45309', background: 'rgba(180,83,9,.06)' }}
            >
              Retry
            </button>
          </div>
        </div>
      ) : !isLoading && !coursesLoading && !hasClasses && !hasCourses ? (
        <div className="dash-card p-4 sm:p-6">
          <div className="py-10 text-center">
            <div className="text-4xl mb-3 opacity-40">📚</div>
            <p className="text-base font-semibold text-astroDark mb-1">Your academy is empty</p>
            <p className="text-sm text-stone-400 mb-4">Create your first course, then add lessons and schedule live classes — everything you teach is organised here.</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setShowCreateCourse(true)}
                className="bg-linear-to-r from-goldAccent to-goldPrimary text-astroDark font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:shadow-lg hover:shadow-goldPrimary/25 transition-all"
              >
                Create your first course
              </button>
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                className="border border-stone-200 text-stone-700 font-bold text-[10px] uppercase tracking-[0.15em] px-5 py-2.5 rounded-xl hover:bg-stone-50 transition-all"
              >
                Schedule a live class
              </button>
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* ══ Section 1 · COURSES — one card per course; each opens its details page ══ */}
          <section className="dash-card p-4 sm:p-6">
            <SectionHead eyebrow="📚 Courses" eyebrowClass="text-amber-700" title="Courses You Teach" count={coursesLoading ? null : courseCount} />
            {coursesLoading || isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {Array.from({ length: 2 }, (_, i) => <ClassSkeletonCard key={i} />)}
              </div>
            ) : coursesError ? (
              <div className="py-6 text-center">
                <p className="text-sm text-stone-400 mb-3">Couldn&rsquo;t load your courses.</p>
                <button
                  type="button"
                  onClick={() => refetchCourses()}
                  className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-widest px-5 py-2.5 rounded-full border border-stone-200 text-stone-600 hover:bg-stone-50"
                >
                  Retry
                </button>
              </div>
            ) : myCourses.length === 0 ? (
              <p className="text-sm text-stone-400 py-4">No courses assigned to you yet — courses you teach appear here with their scheduled classes.</p>
            ) : (
              <div className="space-y-4">
                {myCourses.map((course) => (
                  <CourseCard
                    key={`course-${course.id}`}
                    course={course}
                    sessions={classesByCourseId.get(String(course.id)) || []}
                    onOpen={() => openCourse(course.id)}
                  />
                ))}
              </div>
            )}
          </section>

          {/* ══ Section 2 · LIVE — sessions the instructor hosts ══ */}
          <section className="dash-card p-4 sm:p-6">
            <SectionHead eyebrow="🔴 Live Classes" eyebrowClass="text-red-500" title="Sessions You Host" count={isLoading ? null : liveClasses.length}>
              {/* Filter track scrolls on one row rather than wrapping: a wrapped
                  pill broke out of the rounded track and left the active tab
                  orphaned on its own line once the header got tight. */}
              {!isLoading && liveClasses.length > 0 && (
                <div className="no-scrollbar flex gap-1 overflow-x-auto bg-stone-100/80 rounded-xl p-1 max-w-full sm:shrink-0">
                  {LIVE_FILTERS.map((f) => {
                    const active = f.key === liveFilter
                    return (
                      <button
                        key={f.key}
                        type="button"
                        onClick={() => setLiveFilter(f.key)}
                        className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap px-3 sm:px-3.5 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider transition-all ${active ? 'bg-[#3D0A0A] text-white shadow-md' : 'text-stone-500 hover:text-astroDark hover:bg-white'}`}
                      >
                        {f.label}
                        <span className={`num text-[9px] px-1.5 py-0.5 rounded-full ${active ? 'bg-[#D4A017] text-[#3D0A0A]' : 'bg-stone-200/80 text-stone-500'}`}>
                          {filterCounts[f.key]}
                        </span>
                      </button>
                    )
                  })}
                </div>
              )}
            </SectionHead>
            {isLoading ? (
              <SkeletonGrid />
            ) : liveClasses.length === 0 ? (
              <p className="text-sm text-stone-400 py-4">
                No live sessions — everything has moved to the recordings library below.
              </p>
            ) : visibleLive.length === 0 ? (
              <p className="text-sm text-stone-400 py-4">
                {liveFilter === 'live'
                  ? 'Nothing on air right now.'
                  : liveFilter === 'scheduled'
                    ? 'No sessions scheduled — create a live class to get started.'
                    : 'No past sessions yet.'}
              </p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {visibleLive.map((c) => (
                  <LiveClassCard key={c.key} c={c} onDelete={askDeleteClass} onReschedule={setClassToReschedule} />
                ))}
              </div>
            )}
          </section>

          {/* ══ Section 3 · RECORDED — the recordings library ══ */}
          <section className="dash-card p-4 sm:p-6">
            <SectionHead eyebrow="🎥 Recorded Classes" eyebrowClass="text-amber-700" title="Recordings Library" count={isLoading ? null : recordedClasses.length} />
            {isLoading ? (
              <SkeletonGrid />
            ) : recordedClasses.length === 0 ? (
              <div className="py-8 text-center">
                <div className="text-4xl mb-3 opacity-40">🎬</div>
                <p className="text-base font-semibold text-astroDark mb-1">No recordings yet</p>
                <p className="text-sm text-stone-400">
                  Turn on <span className="font-semibold text-stone-500">&ldquo;Record this class&rdquo;</span> when scheduling a live session — the recording appears here after it ends.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {recordedClasses.map((c) => <RecordedClassCard key={c.key} c={c} onDelete={askDeleteClass} />)}
              </div>
            )}
          </section>
        </>
      )}

      {/* ── Learning side, for accounts that also hold the student role ── */}
      {alsoStudent && (
        <>
          <div className="pt-2">
            <span className="eyebrow text-amber-700">Vedic Academy · Learning</span>
            <h3 className="text-xl text-astroDark mt-1 font-royal">As a Student</h3>
          </div>
          <EnrolledCoursesSections />
        </>
      )}

      {showCreate && <CreateLiveClassModal onClose={() => setShowCreate(false)} />}
      {classToReschedule && (
        <RescheduleLiveClassModal c={classToReschedule} onClose={() => setClassToReschedule(null)} />
      )}
      {showCreateCourse &&
        <CreateCourseModal
          onClose={() => setShowCreateCourse(false)} />
      }
      {classToDelete && (
        <ConfirmDeleteClassModal
          c={classToDelete}
          busy={deleteClassMutation.isPending}
          error={classDeleteError}
          onConfirm={confirmDeleteClass}
          onClose={() => setClassToDelete(null)}
        />
      )}
    </div>
  )
}
